import React from 'react';
import { Link } from 'react-router-dom';
import CodeBlock from '../documentation/CodeBlock';
import { getAuthUrl, getCliPaymentUrl } from '../../data/apiConfig';
import {
    CheckCircle,
    VpnKey,
    CreditCard,
    NotificationsActive,
    Security,
    Store,
    Dns,
    Info,
    Launch
} from '@mui/icons-material';

const IntegrationContent = ({ step, activeIndex = 0, totalSteps = 1 }) => {
    if (!step) return null;

    const renderContent = () => {
        switch (step.id) {
            case 'prerequisites':
                return <PrerequisitesContent />;
            case 'account-setup':
                return <AccountSetupContent />;
            case 'authentication':
                return <AuthenticationContent />;
            case 'payment-flow':
                return <PaymentFlowContent />;
            case 'webhooks':
                return <WebhooksContent />;
            case 'testing':
                return <TestingContent />;
            case 'best-practices':
                return <BestPracticesContent />;
            default:
                return null;
        }
    };

    return (
        <div className="p-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
                <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-xl flex items-center justify-center text-white">
                        {step.Icon ? <step.Icon /> : null}
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">{step.title}</h1>
                        <p className="text-amber-600 font-medium">Durée estimée : {step.duration}</p>
                    </div>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4">
                    <div className="flex items-center justify-between gap-4">
                        <div className="text-sm text-gray-600">
                            Progression
                        </div>
                        <div className="text-sm font-bold text-gray-900">
                            {Math.round(((activeIndex + 1) / totalSteps) * 100)}%
                        </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                        <div
                            className="bg-gradient-to-r from-amber-500 to-yellow-500 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${((activeIndex + 1) / totalSteps) * 100}%` }}
                        />
                    </div>
                </div>
            </div>

            {renderContent()}
        </div>
    );
};

// Sous-composants pour chaque étape
const PrerequisitesContent = () => (
    <div className="space-y-6">
        <div className="bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200 rounded-2xl p-6">
            <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <Info className="text-amber-700" />
                </div>
                <div>
                    <h2 className="text-xl font-bold text-gray-900">Avant de commencer</h2>
                    <p className="text-gray-700 mt-1">
                        Vérifiez ces prérequis pour éviter les blocages lors de l'intégration.
                    </p>
                </div>
            </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl p-6 border border-amber-200">
                <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                        <Store className="text-amber-700" />
                    </div>
                    <h3 className="font-bold text-amber-900">Compte marchand</h3>
                </div>
                <ul className="space-y-2 text-amber-700">
                    <li className="flex items-center space-x-2">
                        <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                        <span>Compte Netikash Business actif</span>
                    </li>
                    <li className="flex items-center space-x-2">
                        <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                        <span>Documents d'entreprise à jour</span>
                    </li>
                </ul>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-200">
                <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                        <Dns className="text-blue-700" />
                    </div>
                    <h3 className="font-bold text-blue-900">Environnement technique</h3>
                </div>
                <ul className="space-y-2 text-blue-700">
                    <li className="flex items-center space-x-2">
                        <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                        <span>Serveur avec HTTPS activé</span>
                    </li>
                    <li className="flex items-center space-x-2">
                        <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                        <span>Accès à l'API Netikash</span>
                    </li>
                </ul>
            </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6">
            <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <Security className="text-amber-700" />
                </div>
                <div className="min-w-0">
                    <h4 className="text-lg font-bold text-gray-900 mb-1">Point de contrôle sécurité</h4>
                    <p className="text-gray-700">
                        Vérifiez que votre domaine est autorisé dans les paramètres de sécurité de votre compte Netikash, et que vos URLs de callback/webhook sont en HTTPS.
                    </p>
                </div>
            </div>
        </div>
    </div>
);

const AccountSetupContent = () => (
    <div className="space-y-6">
        <div className="bg-white border border-gray-200 rounded-2xl p-6">
            <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="text-amber-700" />
                </div>
                <div>
                    <h2 className="text-xl font-bold text-gray-900">Objectif</h2>
                    <p className="text-gray-700 mt-1">
                        Créer une application dans votre espace développeur et récupérer vos identifiants (Client ID / Client Secret).
                    </p>
                </div>
            </div>
        </div>

        <div className="space-y-4">
            <StepNumber number={1} title="Accédez à la section Développeur" />
            <p className="text-gray-700">Connectez-vous à votre compte Netikash et naviguez vers la section "Développeur".</p>

            <StepNumber number={2} title="Créez une nouvelle application" />
            <p className="text-gray-700">Remplissez le formulaire avec les informations de votre application.</p>

            <StepNumber number={3} title="Récupérez vos identifiants" />
            <p className="text-gray-700">Notez précieusement votre Client ID et Client Secret.</p>
        </div>

        <CodeBlock
            code={`// Vos identifiants API
CLIENT_ID = "votre_client_id_ici"
CLIENT_SECRET = "votre_client_secret_ici"`}
            language="javascript"
        />
    </div>
);

const AuthenticationContent = () => (
    <div className="space-y-6">
        <div className="bg-white border border-gray-200 rounded-2xl p-6">
            <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <VpnKey className="text-amber-700" />
                </div>
                <div className="min-w-0">
                    <h2 className="text-xl font-bold text-gray-900">Obtenir un token OAuth2</h2>
                    <p className="text-gray-700 mt-1">
                        Utilisez le flux <span className="font-semibold">client_credentials</span> pour récupérer un access token côté serveur.
                    </p>
                    <div className="mt-4 flex flex-wrap gap-3">
                        <Link to="/authentication" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white font-semibold hover:bg-gray-800 transition-colors">
                            Voir la procédure
                            <Launch fontSize="small" />
                        </Link>
                        <Link to="/documentation?q=authorization" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors">
                            Référence dans la documentation
                        </Link>
                    </div>
                </div>
            </div>
        </div>

        <CodeBlock
            language="bash"
            code={`curl -X POST "${getAuthUrl()}" \\\n  -H "Content-Type: application/json" \\\n  -H "Authorization: Basic base64(client_id:client_secret)" \\\n  -d '{"grant_type":"client_credentials"}'`}
        />
    </div>
);

const PaymentFlowContent = () => (
    <div className="space-y-6">
        <div className="bg-white border border-gray-200 rounded-2xl p-6">
            <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <CreditCard className="text-amber-700" />
                </div>
                <div className="min-w-0">
                    <h2 className="text-xl font-bold text-gray-900">Créer une demande de paiement</h2>
                    <p className="text-gray-700 mt-1">
                        Vous créez une demande, puis vous redirigez l’utilisateur vers le <span className="font-semibold">lien de paiement (redirection client), utilisez l&apos;endpoint POST <code className="bg-gray-100 px-1 rounded">/transactions/requests/cli-payments</code>. Vous créez une demande, puis vous redirigez l&apos;utilisateur vers le champ <code className="bg-gray-100 px-1 rounded">link</code>. Pour un paiement vers un compte spécifique (receiver_id, method), consultez la section Demande de Paiement dans la documentation</span>.
                    </p>
                    <div className="mt-4 flex flex-wrap gap-3">
                        <Link to="/payment-flow" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white font-semibold hover:bg-gray-800 transition-colors">
                            Voir le flux complet
                            <Launch fontSize="small" />
                        </Link>
                        <Link to="/documentation?section=payment-request" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors">
                            Endpoint + payload
                        </Link>
                    </div>
                </div>
            </div>
        </div>

        <CodeBlock
            language="bash"
            code={`curl -X POST "${getCliPaymentUrl()}" \\\n  -H "Authorization: Bearer <access_token>" \\\n  -H "Content-Type: application/json" \\\n  -d '{\n    "amount": 100.5,\n    "currency": "USD",\n    "ref": "CMD-2024-001",\n    "referer_url": "https://votre-site.com/confirmation"\n  }'`}
        />
    </div>
);

const WebhooksContent = () => (
    <div className="space-y-6">
        <div className="bg-white border border-gray-200 rounded-2xl p-6">
            <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <NotificationsActive className="text-amber-700" />
                </div>
                <div className="min-w-0">
                    <h2 className="text-xl font-bold text-gray-900">Recevoir les notifications</h2>
                    <p className="text-gray-700 mt-1">
                        Configurez votre URL webhook dans l’espace développeur et gérez les événements (success, failed, expired...).
                    </p>
                    <div className="mt-4 flex flex-wrap gap-3">
                        <Link to="/documentation?q=webhook" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white font-semibold hover:bg-gray-800 transition-colors">
                            Voir la doc webhooks
                            <Launch fontSize="small" />
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    </div>
);

const TestingContent = () => (
    <div className="space-y-6">
        {/* Contenu similaire aux autres sections */}
        <p>Contenu des tests...</p>
    </div>
);

const BestPracticesContent = () => (
    <div className="space-y-6">
        <div className="bg-white border border-gray-200 rounded-2xl p-6">
            <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <Security className="text-amber-700" />
                </div>
                <div className="min-w-0">
                    <h2 className="text-xl font-bold text-gray-900">Bonnes pratiques</h2>
                    <p className="text-gray-700 mt-1">
                        Gardez vos secrets côté serveur, validez côté backend et traitez les webhooks de façon idempotente.
                    </p>
                    <div className="mt-4 flex flex-wrap gap-3">
                        <Link to="/security" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white font-semibold hover:bg-gray-800 transition-colors">
                            Sécurité & conformité
                            <Launch fontSize="small" />
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    </div>
);

// Composant helper pour les étapes numérotées
const StepNumber = ({ number, title }) => (
    <div className="flex items-center space-x-4">
        <div className="w-8 h-8 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
            {number}
        </div>
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
    </div>
);

export default IntegrationContent;